import "./App.scss";
import { useCallback, useEffect } from "react";
import { useGame } from "./context/GameContext";
import GameOver from "./components/Menus/GameOver/GameOver";
import Candle from "./components/visual/Candle/Candle";
import CustomChessboard from "./components/game/CustomChessboard/CustomChessboard";
import Promotion from "./components/Menus/Promotion/promotionMenu";
import StartMenu from "./components/Menus/StartMenu/startMenu";
import MultiplayerMenu from "./components/multiplayer/MultiplayerMenu/multiplayerMenu";
import StatusScreen from "./components/Menus/StatusScreen/StatusScreen";
import JoinGame from "./components/multiplayer/JoinGameMenu/JoinGame";
import TurnIndicator from "./components/visual/TurnIndicator/TurnIndicator";
import MenuIcon from "./components/Menus/InGameMenus/MenuIcons";
import InGameMenu from "./components/Menus/InGameMenus/InGameMenu";
import { handleKeyDown } from "./utils/navigationHelpers";
import { isUserOnMobile } from "@sinclair-digital/cabin-auth-library";
import CapturePieces from "./components/visual/CapturedPieces/CapturePieces";
import { useMultiplayer } from "./context/MultiplayerContext";
import useUtils from "./hooks/useUtils";
import { Difficulty } from "./types/general";
import { useAfkDetection } from "./hooks/afkDetection";
// import ResponsiveBackground from "./components/Menus/StartMenu/ResponsiveBackground";

/**
 * Handles the main logic for the chess app.
 * Sets up the game state, restarts the game, and renders the game board.
 *
 * @component App
 * @returns The main component for the chess app.
 */
export default function App(): JSX.Element {
    const { gameState, setGameState, status } = useGame();
    const { multiplayerState } = useMultiplayer();
    const { checkForMultiplayer } = useUtils();

    const memoizedHandleKeydown = useCallback((e: KeyboardEvent) => {
        handleKeyDown(e);
    }, []);
    // Only enable AFK detection for active multiplayer games
    const isInMultiplayerGame =
        !gameState.isAIGame && gameState.showGame && multiplayerState.lobbyId !== "";
    useAfkDetection(isInMultiplayerGame);

    /**
     * Load the game state from localStorage when the window loads.
     */
    useEffect(() => {
        checkForMultiplayer();

        // Add event listeners to allow remotes to navigate
        if (!isUserOnMobile()) {
            window.addEventListener("keydown", memoizedHandleKeydown);

            // Focus the first button on the menu
            const buttons = document.querySelectorAll(
                "[data-navrow][data-navcol]",
            )! as NodeListOf<HTMLElement>;
            buttons[0].focus();
        }

        const savedDifficulty = localStorage.getItem("aiDifficulty") as Difficulty;
        if (!savedDifficulty) return;

        setGameState((prevState) => ({
            ...prevState,
            aiDifficulty: savedDifficulty,
        }));
    }, []);

    /**
     * Update the game state to localStorage whenever the game position changes.
     * This saves the game state only for AI games.
     */
    useEffect(() => {
        if (gameState.game && gameState.isAIGame) {
            localStorage.setItem("ai-chessGameState", gameState.game.fen());
            localStorage.setItem("aiDifficulty", gameState.aiDifficulty);
        }
    }, [gameState.game, gameState.aiDifficulty, gameState.isAIGame]);

    /**
     * Preloads all king falling animation frames (white and black kings),
     * and shadows only once per frame to optimize network requests.
     */
    useEffect(() => {
        const totalFrames = 52;

        for (let i = 0; i < totalFrames; i++) {
            const padded = String(i + 1).padStart(2, "0");

            // Preload white king frames
            new Image().src = `/images/kingFall/king-white-${padded}.webp`;
            // Preload black king frames
            new Image().src = `/images/kingFall/king-black-${padded}.webp`;
            // Preload shadow frames (shared)
            new Image().src = `/images/kingFall/king-shadow-${padded}.webp`;
        }
    }, []);

    // Show the loading screen if the user is joining a multiplayer game.
    if (status.showStatusPage) {
        return <StatusScreen />;
    } else if (multiplayerState.showUserNamePage) {
        return <JoinGame />;
    } else if (gameState.showGame && gameState.game) {
        return (
            <div className="chess-app" data-testid="multiplayer-host-chess-app">
                {multiplayerState.isHost && !gameState.isAIGame && (
                    <div
                        id="player-name1"
                        className={`player-name ${gameState.game?.turn() !== "w" && "flashing-turn"}`}
                    >
                        {multiplayerState.p2.username}
                    </div>
                )}

                {multiplayerState.isHost && !gameState.isAIGame && (
                    <div
                        id="player-name2"
                        className={`player-name ${gameState.game?.turn() === "w" && "flashing-turn"}`}
                    >
                        {multiplayerState.p1.username}
                    </div>
                )}
                <TurnIndicator />
                <Candle />
                {!gameState.isPuzzle && <CapturePieces color="white" />}
                <MenuIcon />
                <InGameMenu />
                {!gameState.isPuzzle && <CapturePieces color="black" />}
                {(gameState.game.isGameOver() || multiplayerState.forfeit.isForfeit) &&
                    !gameState.isPuzzle && <GameOver />}
                <Promotion />
                <CustomChessboard />
            </div>
        );
    } else {
        return (
            <div className="chess-app" data-testid="chess-app">
                {/* <ResponsiveBackground /> */}
                <StartMenu />
                <MultiplayerMenu />
            </div>
        );
    }
}
